<template>
  <div v-if="validVersions.length" class="spangle-versions">
    <div class="spangle-versions__header">Select fill</div>
    <div class="spangle-versions__versions-container">
      <div
        v-for="(version, index) in validVersions"
        :key="version.algorithm"
        class="spangle-versions__version"
        :class="{
          'spangle-versions__version--small': validVersions.length > 2,
          'spangle-versions__version--selected': selectedAlgorithm === version.algorithm,
          'spangle-versions__version--placeholder': version.isLoading || version.error
        }"
        @click="selectVersion({ version })"
      >
        <template v-if="version.isLoading || version.error">
          <Spinner v-if="version.isLoading" class="spangle-versions__version-loader" />
          <UseSvg
            v-else-if="version.error"
            :icon="'strong-info'"
            class="spangle-versions__version-error"
          />
          <div class="spangle-versions__version-text">
            {{ versionLoaderText(version) }}
          </div>
        </template>
        <template v-else>
          <div class="spangle-versions__version-text">Pattern {{ index + 1 }}</div>
          <div
            class="spangle-versions__version-background"
            :class="{
              'spangle-versions__version-background-transparent': isTransparent
            }"
          >
            <img
              class="spangle-versions__version-image"
              :src="version.imageUrl"
              :alt="`Spangle Pattern : ${index + 1}`"
            />
          </div>
          <div class="spangle-versions__version-preview-text">Preview</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import Spinner from "components/jiffy/vue-cart/Spinner.vue";
import UseSvg from "common/vue/components/UseSvg.vue";
import { useDtfStore } from "./store";
import { useSpanglesStore } from "./spangles-module";

export default {
  name: "DtfSpangleVersions",
  components: {
    Spinner,
    UseSvg
  },
  computed: {
    ...mapState(useDtfStore, ["artworkBackground"]),
    ...mapState(useSpanglesStore, ["selectedAlgorithm", "spangleVariations"]),
    isTransparent() {
      return this.artworkBackground === "transparent";
    },
    validVersions() {
      return this.spangleVariations.filter(version => !version.error);
    }
  },
  methods: {
    ...mapActions(useSpanglesStore, ["selectVersion"]),
    versionLoaderText(version) {
      if (version.error) return "Error loading pattern";
      return "Version in progress...";
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.spangle-versions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-family: $font-family-inter;
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 16px;

  &__header {
    font-weight: bold;
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 10px;
  }

  &__versions-container {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    overflow-y: hidden;
    width: 100%;
  }

  &__version {
    font-family: inherit;
    font-size: inherit;
    display: flex;
    flex-direction: column;
    align-items: center;
    border: 1px solid $gray-border-light;
    border-radius: 8px;
    min-width: 140px;
    min-height: 124px;
    justify-content: space-between;
    cursor: pointer;
    overflow: hidden;

    @include with-tablet {
      min-width: 208px;
    }

    &--selected {
      border: 2px solid $borneo-pink-gradient-start;
    }

    &--placeholder {
      justify-content: center;
    }

    &--small {
      min-width: 140px;
      min-height: 120px;
    }
  }

  &__version-text {
    color: $dove-gray;
    font-family: inherit;
    font-weight: 400;
    font-size: 12px;
    padding-top: 8px;
    width: 100%;
    text-align: center;
    background-color: $white;
  }

  &__version-image {
    width: auto;
    height: auto;
    max-width: 60px;
    max-height: 60px;
  }

  &__version-background {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: v-bind(artworkBackground);
  }

  &__version-background-transparent {
    background: $transparent-bg;
  }

  &__version-loader {
    width: 30px;
    height: 30px;
  }

  &__version-error {
    width: 20px;
    height: 20px;
    color: $dove-gray;
  }

  &__version-preview-text {
    color: $white;
    background-color: $borneo-pink-gradient-start;
    font-family: inherit;
    font-weight: inherit;
    font-size: 12px;
    line-height: 24px;
    width: 100%;
    text-align: center;
  }
}
</style>
